/**
 * Banner Reposition Overlay — intuitive vertical drag to adjust banner crop.
 *
 * How objectPosition works:
 *   objectPosition: "center 0%"   → shows TOP of image
 *   objectPosition: "center 50%"  → shows MIDDLE of image
 *   objectPosition: "center 100%" → shows BOTTOM of image
 *
 * Drag behavior (intuitive — matches phone photo viewer):
 *   Drag UP   → reveal lower part of image → posY INCREASES
 *   Drag DOWN → reveal upper part of image → posY DECREASES
 *   This is the natural "grab and move" feeling.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, X, GripVertical } from 'lucide-react'
import { supabase } from '@turnifly/shared/api'
import { useAuthStore } from '@turnifly/shared/stores'
import { BannerPresetBackground } from './habit-banner'

interface BannerRepositionOverlayProps {
  bannerUrl: string | null
  bannerTemplate: string
  initialPositionY: number
  onClose: () => void
}

export const BannerRepositionOverlay: React.FC<BannerRepositionOverlayProps> = ({
  bannerUrl,
  bannerTemplate,
  initialPositionY,
  onClose,
}) => {
  const { t } = useTranslation()
  const { user, setUser } = useAuthStore()
  const [posY, setPosY] = useState(initialPositionY)
  const [isSaving, setIsSaving] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)
  const lastClientY = useRef(0)
  const scrollElRef = useRef<HTMLElement | null>(null)
  const scrollPosRef = useRef(0)

  // On mount: capture scroll position and lock scrolling
  useEffect(() => {
    const scrollEl = containerRef.current?.closest('.overflow-y-auto') as HTMLElement | null
    scrollElRef.current = scrollEl
    scrollPosRef.current = scrollEl?.scrollTop ?? window.scrollY

    // Lock body scroll during reposition
    document.body.style.overflow = 'hidden'
    return (): void => {
      document.body.style.overflow = ''
    }
  }, [])

  const restoreScroll = useCallback((): void => {
    requestAnimationFrame(() => {
      if (scrollElRef.current) {
        scrollElRef.current.scrollTop = scrollPosRef.current
      } else {
        window.scrollTo(0, scrollPosRef.current)
      }
    })
  }, [])

  // Use touch events for mobile (more reliable than pointer events in Telegram Mini App)
  const handleStart = useCallback((clientY: number): void => {
    isDragging.current = true
    lastClientY.current = clientY
  }, [])

  const handleMove = useCallback((clientY: number): void => {
    if (!isDragging.current || !containerRef.current) return

    const deltaY = clientY - lastClientY.current
    lastClientY.current = clientY

    const containerHeight = containerRef.current.getBoundingClientRect().height
    const sensitivity = 100 / containerHeight
    const deltaPercent = -deltaY * sensitivity

    setPosY((prev) => Math.round(Math.max(0, Math.min(100, prev + deltaPercent))))
  }, [])

  const handleEnd = useCallback((): void => {
    isDragging.current = false
  }, [])

  const onTouchStart = useCallback((e: React.TouchEvent): void => {
    handleStart(e.touches[0].clientY)
  }, [handleStart])

  const onTouchMove = useCallback((e: React.TouchEvent): void => {
    e.preventDefault()
    handleMove(e.touches[0].clientY)
  }, [handleMove])

  const onMouseDown = useCallback((e: React.MouseEvent): void => {
    handleStart(e.clientY)
  }, [handleStart])

  const onMouseMove = useCallback((e: React.MouseEvent): void => {
    handleMove(e.clientY)
  }, [handleMove])

  const handleCancel = useCallback((): void => {
    onClose()
    restoreScroll()
  }, [onClose, restoreScroll])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!user) return
    setIsSaving(true)
    try {
      await supabase
        .from('profiles')
        .update({ banner_position_y: posY })
        .eq('id', user.id)
        .throwOnError()

      setUser({ ...user, banner_position_y: posY })
      onClose()
      restoreScroll()
    } catch {
      // User can retry
    } finally {
      setIsSaving(false)
    }
  }, [user, posY, setUser, onClose, restoreScroll])

  return (
    <div
      ref={containerRef}
      className="relative w-full h-[160px] lg:h-[200px] rounded-2xl overflow-hidden border-2 border-primary/50 select-none"
      style={{ touchAction: 'none' }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={handleEnd}
      onTouchCancel={handleEnd}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
    >
      {/* Banner image */}
      {bannerUrl ? (
        <img
          src={bannerUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: `center ${posY}%` }}
          draggable={false}
        />
      ) : (
        <BannerPresetBackground template={bannerTemplate} positionY={posY} />
      )}

      {/* Dark overlay with drag hint */}
      <div className="absolute inset-0 bg-black/30 flex items-center justify-center pointer-events-none">
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm text-white/80 text-[12px] font-medium">
          <GripVertical size={14} />
          {t('home.bannerDragHint')}
        </div>
      </div>

      {/* Save / Cancel buttons — pointer-events restored */}
      <div className="absolute top-2.5 right-2.5 z-10 flex items-center gap-1.5 pointer-events-auto">
        <button
          onClick={handleCancel}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          disabled={isSaving}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm text-white/70 hover:text-white hover:bg-danger/60 transition-colors"
        >
          <X size={16} />
        </button>
        <button
          onClick={handleSave}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          disabled={isSaving}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-primary/80 backdrop-blur-sm text-white hover:bg-primary transition-colors"
        >
          <Check size={16} />
        </button>
      </div>
    </div>
  )
}
